/**
 * Persist client-link session tokens so a normal browser refresh can resume
 * without the one-time URL hash token (F-03).
 */
export type ClientLinkSessionRecord = {
  readonly clientLinkId: string;
  readonly sessionToken: string;
  readonly expiresAt: string;
};

const STORAGE_PREFIX = "enrein.clientLinkSession.v1:";

function storageKey(clientLinkId: string): string {
  return `${STORAGE_PREFIX}${clientLinkId}`;
}

export function readStoredClientLinkSession(
  clientLinkId: string,
  storage: Pick<Storage, "getItem"> = typeof sessionStorage !== "undefined" ? sessionStorage : { getItem: () => null },
  nowMs: number = Date.now(),
): ClientLinkSessionRecord | null {
  const raw = storage.getItem(storageKey(clientLinkId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ClientLinkSessionRecord>;
    if (
      typeof parsed.clientLinkId !== "string" ||
      parsed.clientLinkId !== clientLinkId ||
      typeof parsed.sessionToken !== "string" ||
      parsed.sessionToken.length < 16 ||
      typeof parsed.expiresAt !== "string"
    ) {
      return null;
    }
    const exp = Date.parse(parsed.expiresAt);
    if (!Number.isFinite(exp) || exp <= nowMs) return null;
    return {
      clientLinkId: parsed.clientLinkId,
      sessionToken: parsed.sessionToken,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

export function writeStoredClientLinkSession(
  record: ClientLinkSessionRecord,
  storage: Pick<Storage, "setItem"> = typeof sessionStorage !== "undefined"
    ? sessionStorage
    : { setItem: () => undefined },
): void {
  storage.setItem(storageKey(record.clientLinkId), JSON.stringify(record));
}

export function clearStoredClientLinkSession(
  clientLinkId: string,
  storage: Pick<Storage, "removeItem"> = typeof sessionStorage !== "undefined"
    ? sessionStorage
    : { removeItem: () => undefined },
): void {
  storage.removeItem(storageKey(clientLinkId));
}
